import React, { useState, useEffect } from 'react';
import { localePath } from '../config/region';
import { useParams, useLocation } from 'react-router-dom';
import { API_URLS } from '../config/api';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiUtils';
import { reinitializeSceneIds } from '../utils/novelUtils';
import ParticleBackground from './ParticleBackground';
import './StaticSceneDetail.css';
import StaticSceneInfo from './static_scene/StaticSceneInfo';
import StaticSceneNextScenes from './static_scene/StaticSceneNextScenes';
import StaticScenePreview from './static_scene/StaticScenePreview';
import CustomSelect from './CustomSelect';

const CATEGORIES = [
  { id: 'info', label: 'Информация', icon: '📖' },
  { id: 'nextScenes', label: 'След. сцены', icon: '🔗' },
  { id: 'preview', label: 'Предпросмотр', icon: '👁️' }
];

const getInitialTab = () => {
  const tab = new URLSearchParams(window.location.search).get('tab');
  return CATEGORIES.some(c => c.id === tab) ? tab : 'info';
};

const StaticSceneDetail = () => {
  const { novelId, sceneId } = useParams();
  const location = useLocation();

  // Detect production mode from URL
  const isProduction = window.location.pathname.includes('production_change');
  const backHref = localePath(`/${isProduction ? 'production_change' : 'building'}/${novelId}`) + '?category=staticScenes';
  const [scene, setScene] = useState(null);
  const [novel, setNovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(getInitialTab);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyName, setCopyName] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);

  // Fetch scene and novel data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch novel data first
        const apiEndpoint = isProduction ? API_URLS.PRODUCTION_NOVEL_BY_ID(novelId) : `${API_URLS.BUILDING_NOVELS}/${novelId}`;
        const novelResponse = await apiGet(apiEndpoint);
        // Handle different response structures for production vs building
        const novelData = isProduction ?
          (novelResponse.data.full_novel ? {
            ...novelResponse.data.full_novel,
            id: novelResponse.data.full_novel.id || novelResponse.data.full_novel._id
          } : novelResponse.data) :
          novelResponse.data;
        setNovel(novelData);

        // Find the scene in the novel data
        const sceneData = novelData.static_scenes?.find(s => s.id === sceneId);
        if (!sceneData) {
          throw new Error('Сцена не найдена');
        }
        setScene(sceneData);

      } catch (err) {
        setError(err.message || 'Ошибка загрузки сцены');
      } finally {
        setLoading(false);
      }
    };

    if (novelId && sceneId) {
      fetchData();
    }
  }, [novelId, sceneId]);

  // Sync active tab with the ?tab= query param (e.g. when navigating to a next scene)
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab && CATEGORIES.some(c => c.id === tab)) setSelectedCategory(tab);
  }, [location.search, sceneId]);



  const openCopyModal = () => {
    setCopyName(`Копия: ${scene?.name || ''}`);
    setShowCopyModal(true);
  };

  const handleCopyConfirm = async () => {
    if (!copyName.trim() || !scene || copyLoading) return;

    try {
      setCopyLoading(true);
      setError(null);

      const createEndpoint = isProduction
        ? API_URLS.PRODUCTION_STATIC_SCENES(novelId)
        : API_URLS.BUILDING_STATIC_SCENES(novelId);

      const createResponse = await apiPost(createEndpoint, { name: copyName.trim(), tag: scene.tag });
      // Use the id the backend reports for the scene it just created. Diffing the
      // returned scene list can pick the wrong scene (and then overwrite it) when
      // the local list is stale; only fall back to that if the header is missing.
      const existingIds = new Set((novel?.static_scenes || []).map(s => s.id));
      const newSceneId = createResponse.headers?.['x-new-scene-id']
        || createResponse.data.static_scenes?.find(s => !existingIds.has(s.id))?.id;
      if (!newSceneId) throw new Error('Не удалось найти созданную сцену');

      const updateEndpoint = isProduction
        ? API_URLS.PRODUCTION_STATIC_SCENE(novelId, newSceneId)
        : API_URLS.BUILDING_STATIC_SCENE(novelId, newSceneId);

      const sceneCopy = reinitializeSceneIds(scene);
      // Offset the copy to the right of the original so it doesn't stack on the
      // same node in the graph (overlapping nodes make links/clicks ambiguous).
      // Only when the source has real stored coordinates, though: falling back to
      // 0 put every copy of a coordinate-less scene at exactly (250, 0), piling
      // them into one opaque white blob. null lets the graph auto-place the copy.
      const hasCoords = typeof scene.x === 'number' && typeof scene.y === 'number';
      sceneCopy.x = hasCoords ? scene.x + 250 : null;
      sceneCopy.y = hasCoords ? scene.y : null;
      await apiPut(updateEndpoint, { ...sceneCopy, id: newSceneId, name: copyName.trim() });

      const prefix = isProduction ? 'production_change' : 'building';
      window.location.href = localePath(`/${prefix}/${novelId}/static_scene/${newSceneId}`);

    } catch (err) {
      setError(err.message || 'Не удалось скопировать сцену');
      setCopyLoading(false);
      setShowCopyModal(false);
    }
  };

  const handleDelete = async () => {
    if (!scene || loading) return;

    const confirmDelete = window.confirm('Вы уверены, что хотите удалить эту сцену?');
    if (!confirmDelete) return;

    try {
      setLoading(true);
      setError(null);

      const deleteEndpoint = isProduction ?
        API_URLS.PRODUCTION_STATIC_SCENE(novelId, sceneId) :
        API_URLS.BUILDING_STATIC_SCENE(novelId, sceneId);
      await apiDelete(deleteEndpoint);

      window.location.href = backHref;

    } catch (err) {
      setError(err.message || 'Не удалось удалить сцену');
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="isd-wrap">
        <ParticleBackground />
        <div className="isd-app">
          <div className="isd-loading">
            <div className="isd-spinner"></div>
            <p>Загрузка...</p>
          </div>
        </div>
      </div>
    );
  }

  const commonProps = { novelId, sceneId, scene, setScene, novel, loading, setLoading, error, setError, isProduction };

  const categoryComponents = {
    info: <StaticSceneInfo {...commonProps} />,
    nextScenes: <StaticSceneNextScenes {...commonProps} />,
    preview: <StaticScenePreview novelId={novelId} scene={scene} novel={novel} loading={loading} isProduction={isProduction} />,
  };

  return (
    <div className="isd-wrap">
      <ParticleBackground />
      <div className="isd-app">
        <div className="isd-header">
          <a href={backHref} className="isd-back">
            ← Назад к статическим сценам
          </a>
          <h1 className="isd-title">{scene?.name || 'Детали статической сцены'}</h1>
          <div className="isd-header-actions">
            <button
              className="isd-btn-secondary"
              onClick={openCopyModal}
              disabled={loading}
            >
              Копировать сцену
            </button>
            <button
              className="isd-btn-danger"
              onClick={handleDelete}
              disabled={loading}
            >
              Удалить сцену
            </button>
          </div>
        </div>

        {isProduction && (
          <div className="isd-prod-warning">
            ⚠️ Это продакшн-новелла, будьте осторожны ⚠️
          </div>
        )}

        {error && <div className="isd-error">{error}</div>}

        <CustomSelect
          className="isd-cat-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          {CATEGORIES.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.label}</option>
          ))}
        </CustomSelect>

        <div className="isd-body">
          <div className="isd-sidebar">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                className={`isd-sidebar-btn${selectedCategory === category.id ? ' active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-label">{category.label}</span>
              </button>
            ))}
          </div>

          <div className="isd-main">
            {categoryComponents[selectedCategory]}
          </div>
        </div>
      </div>

      {showCopyModal && (
        <div className="isd-overlay">
          <div className="isd-modal">
            <div className="isd-modal-head">
              <h3 className="isd-modal-title">Копировать сцену</h3>
              <button className="isd-modal-close" onClick={() => setShowCopyModal(false)}>×</button>
            </div>
            <div className="isd-modal-body">
              <div className="isd-form-group">
                <label className="isd-form-label">Название копии</label>
                <input
                  type="text"
                  value={copyName}
                  onChange={(e) => setCopyName(e.target.value)}
                  className="isd-input"
                  disabled={copyLoading}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleCopyConfirm()}
                />
              </div>
            </div>
            <div className="isd-modal-footer">
              <button className="isd-btn-ghost" onClick={() => setShowCopyModal(false)} disabled={copyLoading}>Отмена</button>
              <button className="isd-btn-secondary" onClick={handleCopyConfirm} disabled={copyLoading || !copyName.trim()}>
                {copyLoading ? 'Копирование...' : 'Копировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaticSceneDetail;
